import * as React from "react";
import { cn } from "../lib/cn";

export interface CompanionHeroProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Leading icon (Lucide component or any node). */
  icon?: React.ReactNode;
  /** Body content — usually a short sentence with `<strong>` highlights. */
  children?: React.ReactNode;
  /** Optional CTA button rendered on the right. */
  cta?: React.ReactNode;
}

/**
 * CompanionHero — STEP A on the v18 mockup.
 *
 * Glassmorph card with an orange→violet 3-px brand strip on the left,
 * a small icon chip, body text, and an optional CTA pinned right.
 * Shows up momentarily after Ando answers, before fading into the chat
 * scroll. Scaffolding only — animation token (`companion-in`) is wired,
 * actual ChatGPT-style behaviour ships in feature work.
 */
export const CompanionHero = React.forwardRef<HTMLDivElement, CompanionHeroProps>(
  ({ icon, children, cta, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        // v18 mockup `.companion-hero` — glass tint, 12px radius, black/5
        // border, ultra-light shadow. Jul 2026 — vertical padding trimmed
        // (py-3.5 → py-2.5) as part of the hub density pass; px-[18px] stays.
        "relative flex animate-companion-in items-center gap-3.5 overflow-hidden rounded-xl border border-black/5 bg-white/55 px-[18px] py-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.02)]",
        // Brand strip — orange→violet gradient bar on the left
        "before:absolute before:top-0 before:bottom-0 before:left-0 before:w-[3px] before:rounded-r before:bg-[linear-gradient(180deg,#FF7A1A_0%,#9B5DE5_100%)]",
        className
      )}
      {...rest}
    >
      {icon ? (
        <span
          className="ch-icon flex shrink-0 items-center justify-center text-ink-3 [&_svg]:[stroke-width:1.5]"
          aria-hidden
        >
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 font-medium text-[13px] text-ink-1 leading-[1.45] tracking-[-0.005em] [&_strong]:font-semibold">
        {children}
      </span>
      {cta ? <span className="shrink-0">{cta}</span> : null}
    </div>
  )
);
CompanionHero.displayName = "CompanionHero";
